import { useEffect, useState } from 'react';

/** Evento do Chrome/Android que permite mostrar o "Instalar app" no momento certo. */
interface PedidoInstalacao extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let pedido: PedidoInstalacao | null = null;
const ouvintes = new Set<() => void>();
const avisar = () => ouvintes.forEach((f) => f());

/** Registra o service worker (só no build de produção) e guarda o pedido de instalação. */
export function prepararApp() {
  if (typeof window === 'undefined') return;
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    pedido = e as PedidoInstalacao;
    avisar();
  });
  window.addEventListener('appinstalled', () => {
    pedido = null;
    avisar();
  });
  if (import.meta.env.PROD && 'serviceWorker' in navigator)
    window.addEventListener('load', () => {
      void navigator.serviceWorker.register('/sw.js').catch(() => undefined);
    });
}

export function jaInstalado(): boolean {
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}

export function ehIphone(): boolean {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/** Estado do botão "Instalar app": Android/Chrome com pedido, iPhone com instrução, ou nada. */
export function useInstalarApp() {
  const [, setN] = useState(0);
  useEffect(() => {
    const f = () => setN((n) => n + 1);
    ouvintes.add(f);
    return () => void ouvintes.delete(f);
  }, []);
  if (jaInstalado()) return { modo: 'instalado' as const };
  if (pedido)
    return {
      modo: 'pedido' as const,
      instalar: async () => {
        const p = pedido;
        if (!p) return;
        await p.prompt();
        await p.userChoice.catch(() => undefined);
        pedido = null;
        avisar();
      },
    };
  if (ehIphone()) return { modo: 'iphone' as const };
  return { modo: 'indisponivel' as const };
}
